<template>
  <fn-page-overlay
    :keep-body-scroll-position="keepScrollPosition"
    @click="handleOverlayClick"
  >
    <div
      ref="modal"
      aria-modal
      role="dialog"
      class="modal"
      :class="[
        {
          'modal--secondary': secondary,
          'modal--full-screen-mobile': fullScreenMobile,
          'modal--small-on-desktop': smallOnDesktop,
          'modal--form': isForm,
          'modal--border-radius-xl': isForm,
          'modal--rebrand': rebrand,
          'modal--adjustable-height': adjustableHeight,
        },
        maxWidth ? `modal--size-${maxWidth}` : '',
      ]"
    >
      <fn-loading-overlay v-if="loading" />

      <slot name="beforeContent" />

      <div class="m-header">
        <div
          v-if="backButton"
          class="m-back"
        >
          <fn-button
            id="modal-back-close"
            class="m-back"
            plain
            inline
            :aria-label="$t('accessibility.general.closeModal')"
            @click.prevent="$emit('close')"
            @keydown.enter.prevent="$emit('close')"
            @keydown.space.prevent="$emit('close')"
          >
            <fn-icon
              icon="arrow-left"
              color="orientation-green"
              size="s"
            />
          </fn-button>
        </div>
        <div
          v-if="headlineText"
          class="headline-bar"
        >
          <fn-headline
            type="h2"
            size="h3"
            class="modal-head"
            :color="headlineColor"
            :icon="headlineIcon"
          >
            <span class="headline-wrapper">
              {{ headlineText }}
            </span>
          </fn-headline>
        </div>
        <div
          v-if="closable && !backButton"
          :class="['m-actions', { 'm-actions--no-border': fullWidthHeaderDivider }]"
        >
          <fn-button
            id="modal-close"
            class="m-close"
            plain
            :aria-label="$t('accessibility.general.closeModal')"
            @click.prevent="$emit('close')"
            @keydown.enter.prevent="$emit('close')"
            @keydown.space.prevent="$emit('close')"
          >
            <fn-icon icon="cross" />
          </fn-button>
        </div>
        <fn-divider
          v-if="fullWidthHeaderDivider"
          class="m-divider"
        />
      </div>
      <div class="m-content">
        <slot />
      </div>
    </div>
  </fn-page-overlay>
</template>

<script>
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap';

export default defineComponent({
  props: {
    secondary: {
      type: Boolean,
    },
    fullScreenMobile: {
      type: Boolean,
    },
    isForm: {
      type: Boolean,
    },
    closable: {
      type: Boolean,
      default: true,
    },
    loading: {
      type: Boolean,
    },
    headlineText: {
      type: String,
      default: '',
    },
    headlineIcon: {
      type: String,
      default: '',
    },
    headlineColor: {
      type: String,
      default: '',
    },
    smallOnDesktop: {
      type: Boolean,
    },
    borderRadiusXl: {
      type: Boolean,
    },
    rebrand: {
      type: Boolean,
    },
    backButton: {
      type: Boolean,
    },
    maxWidth: {
      type: String,
      default: '',
    },
    allClose: {
      type: Boolean,
      default: true,
    },
    keepScrollPosition: {
      type: Boolean,
    },
    fullWidthHeaderDivider: {
      type: Boolean,
    },
    adjustableHeight: {
      type: Boolean,
    },
  },
  emits: ['close'],
  setup(props, { emit }) {
    const focusTrapInstance = useTemplateRef('modal');
    const { hasFocus, activate, deactivate } = useFocusTrap(focusTrapInstance, { immediate: true });
    useEscClose(() => emit('close'));

    return {
      focusTrapInstance,
      hasFocus,
      activate,
      deactivate,
    };
  },
  mounted() {
    // Activate focus trap when the modal is mounted
    // also catch if focustrapinstance is a function
    if (typeof this.focusTrapInstance?.activate === 'function') {
      this.activateFocusTrap();
    }
  },
  beforeUnmount() {
    if (this.focusTrapInstance?.deactivate) {
      this.focusTrapInstance.deactivate();
    }
  },
  methods: {
    handleOverlayClick(event) {
      if (this.allClose && this.$el.contains(event.target)) {
        this.$emit('close');
      }
    },
    activateFocusTrap() {
      this.focusTrapInstance?.activate();
    },
    deactivateFocusTrap() {
      this.focusTrapInstance?.deactivate();
    },
  },
});
</script>

<style lang="scss" scoped>
$modalSizes: (
  'default': $content-max-width,
  's': 373px,
  'm': 400px,
  'l': 570px,
  'xl': 810px,
);

@function modal-size($size) {
  @return map-get($modalSizes, $size);
}

.modal {
  position: relative;
  display: block;
  background-color: $color-general-background-neutral-default;
  box-shadow: 0 2px 10px 0 rgba(palette(black), 0.15);
  border-radius: map-get($border-radiuses, 'rebrand');
  width: 100%;
  max-width: $content-max-width;
  margin: auto;
  transition: all 0.3s ease-in-out;

  @include bp(m) {
    max-width: modal-size('default');
  }

  .m-actions {
    display: flex;
    justify-content: flex-end;
    padding: math.div($pad, 2) 0 math.div($pad, 2) math.div($pad, 2);
    border-bottom: 1px solid palette(sand, light);

    &.m-actions--no-border {
      border: none;
    }
  }

  .m-close {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    width: 30px;
    height: 30px;
    color: palette(black);
    text-decoration: none;
    margin-right: 5px;
  }

  .m-content {
    padding: $pad;

    @include bp(m) {
      padding: $pad * 2;
    }
  }

  .m-header {
    position: relative;
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: center;
    padding: $pad $pad 0;
    z-index: 2;

    @include bp(m) {
      padding: $pad $pad * 2 0;
    }
  }

  .m-divider {
    grid-column: span 2;
    min-height: unset;
  }

  &.modal--secondary {
    background-color: $color-brand-fnmz-green;
    color: $color-general-text-inverse-default;

    .m-actions {
      border-bottom-color: palette(green);
    }

    .m-close {
      color: $color-general-text-inverse-default;
    }
  }

  &.modal--full-screen-mobile {
    @include bp(m, max) {
      min-height: 100%;
      border-radius: 0;
      overflow-y: scroll;
    }

    .m-content {
      @include bp(m, max) {
        padding: $pad;
        background-color: $color-general-background-neutral-default;
      }
    }
  }

  &.modal--size-s {
    max-width: modal-size('s');
  }

  &.modal--size-m {
    max-width: modal-size('m');
  }

  &.modal--size-l,
  &.modal--form {
    max-width: modal-size('l');
  }

  &.modal--size-xl {
    max-width: modal-size('xl');
  }

  &.modal--border-radius-xl {
    border-radius: map-get($border-radiuses, 'xl');
  }

  &.modal--small-on-desktop {
    flex: 0 0 auto;

    @include bp(m) {
      max-width: modal-size('m');
    }

    .m-actions {
      border-bottom: 0;
    }

    .m-content {
      padding-top: 0;
    }
  }

  h2.heading.modal-head.h1 {
    float: left;
    width: 88%;
    margin: $space $space 0 math.div($space, 0.75);
    color: $color-brand-fnmz-green;
    padding-bottom: math.div($pad, 2);
    line-height: map-get($font-line-heights, 's');
  }

  @include bp(m, max) {
    h2.heading.modal-head.h1 {
      width: 80%;
      margin: $space * 0.75 math.div($space, 2) 0 math.div($space, 2);
      position: relative;
    }

    .headline-wrapper {
      position: absolute;
      top: 10px;
      left: 48px;
      font-size: map-get($font-sizes-headings, 'm');

      @include bp(m) {
        top: 0;
      }
    }
  }

  &.modal--rebrand {
    padding: $space * 2.1 $pad $pad;

    @include bp(m) {
      padding: $pad * 2 $pad * 2 $ui-height;
      border-radius: map-get($border-radiuses, 'xxl');
    }

    .m-header {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0 0 $pad;
      margin-bottom: $space;

      &:after {
        position: absolute;
        content: '';
        height: 1px;
        width: 100%;
        left: 0;
        bottom: 0;
        background: palette(grey, lighter);
      }

      .m-back {
        position: absolute;
        left: 0;
        top: 0;

        @include bp(m) {
          top: 10px;
        }
      }

      .headline-wrapper {
        position: static;
      }
    }

    .m-content {
      padding: 0;
    }
  }

  &.modal--adjustable-height {
    max-height: 90vh;
    animation: slideIn 1s forwards;

    @include bp(m, max) {
      max-height: unset;
      height: 100%;
    }

    @keyframes slideIn {
      from {
        transform: translateY($space);
        opacity: 0;
      }
      to {
        transform: translateY(0);
        opacity: 1;
      }
    }
  }
}
</style>
